import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../nav_bar/Navbar";
import Footer from "../footer/Footer";
import "./StudyHub.css";

const SECTIONS = [
  {
    id: "reading-gd",
    emoji: "📖",
    title: "Reading & Group Discussion",
    accent: "#028891",
    items: [
      { emoji: "📖", title: "Reading Passages", description: "Articles, questions & learning", accent: "#028891", path: "/study/reading" },
      { emoji: "🗣️", title: "Group Discussion", description: "GD topics with key points", accent: "#15803D", path: "/gd" },
    ],
  },
  {
    id: "vocabulary",
    emoji: "📚",
    title: "Vocabulary & Words",
    accent: "#AB47BC",
    items: [
      { emoji: "📖", title: "Vocabulary", description: "Expand your word power", accent: "#AB47BC", path: "/study/vocabulary" },
      { emoji: "📚", title: "Vocab Groups", description: "Words by topic & category", accent: "#43A047", path: "/study/vocabulary/groups" },
      { emoji: "🔤", title: "Similar Words", description: "Confusing words made clear", accent: "#EF5350", path: "/study/similar-words" },
      { emoji: "🔤", title: "Homonyms", description: "One word, multiple meanings", accent: "#7E57C2", path: "/study/homonyms" },
      { emoji: "📝", title: "Phrases", description: "Common phrases & abbreviations", accent: "#8BC34A", path: "/study/phrases" },
    ],
  },
  {
    id: "grammar",
    emoji: "✍️",
    title: "Grammar & Sentences",
    accent: "#26A69A",
    items: [
      { emoji: "📝", title: "Grammar Zone", description: "Master English grammar rules", accent: "#26A69A", path: "/study/grammar" },
      { emoji: "💬", title: "Daily Sentences", description: "Everyday phrases you need", accent: "#42A5F5", path: "/study/daily-sentences" },
      { emoji: "🏢", title: "Official Sentences", description: "Professional & formal English", accent: "#5C6BC0", path: "/study/official-sentences" },
      { emoji: "✍️", title: "Practice Sentences", description: "Read & practice real sentences", accent: "#009688", path: "/study/practice-sentences" },
    ],
  },
  {
    id: "speaking",
    emoji: "🗣️",
    title: "Speaking & Pronunciation",
    accent: "#00ACC1",
    items: [
      { emoji: "🗣️", title: "IPA Phonetics", description: "Master pronunciation with IPA", accent: "#00ACC1", path: "/study/ipa/symbols" },
      { emoji: "👅", title: "Tongue Twisters", description: "Improve pronunciation & fluency", accent: "#FFA726", path: "/study/tongue-twisters" },
      { emoji: "⚡", title: "Quick Boost", description: "Short lessons for fast learning", accent: "#FF7043", path: "/study/quick-boost" },
    ],
  },
];

/* ── Animated accordion content ── */
const AccordionContent = ({ open, children }) => {
  const ref = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!ref.current) return;
    if (open) {
      setHeight(ref.current.scrollHeight);
    } else {
      setHeight(0);
    }
  }, [open]);

  return (
    <div
      className="sh-accordion-body"
      style={{ maxHeight: open ? height : 0 }}
    >
      <div ref={ref}>{children}</div>
    </div>
  );
};

/* ── Item card ── */
const ItemCard = ({ item, onClick }) => (
  <div className="sh-card" onClick={onClick} style={{ "--accent": item.accent }}>
    <div className="sh-card-bar" />
    <div className="sh-card-inner">
      <div className="sh-card-icon">{item.emoji}</div>
      <div className="sh-card-text">
        <div className="sh-card-title">{item.title}</div>
        <div className="sh-card-desc">{item.description}</div>
      </div>
    </div>
  </div>
);

/* ── Section accordion ── */
const Section = ({ section, open, onToggle, navigate }) => (
  <div className={`sh-section ${open ? "sh-section--open" : ""}`}>
    <button
      className="sh-section-header"
      onClick={onToggle}
      style={{ "--accent": section.accent }}
    >
      <div className="sh-section-bar" />
      <div className="sh-section-row">
        <div className="sh-section-icon">{section.emoji}</div>
        <span className="sh-section-title">{section.title}</span>
        <span className="sh-section-count">{section.items.length}</span>
        <span className={`sh-chevron ${open ? "sh-chevron--open" : ""}`}>
          {open ? "▲" : "▼"}
        </span>
      </div>
    </button>

    <AccordionContent open={open}>
      <div className="sh-grid">
        {section.items.map((item) => (
          <ItemCard key={item.title} item={item} onClick={() => navigate(item.path)} />
        ))}
      </div>
    </AccordionContent>
  </div>
);

/* ── Main page ── */
const StudyHub = () => {
  const navigate = useNavigate();
  const [openId, setOpenId] = useState(SECTIONS[0].id);

  const toggle = (id) => setOpenId((prev) => (prev === id ? null : id));

  return (
    <>
      <Navbar />
      <div className="sh-page">

        {/* Hero */}
        <div className="sh-hero">
          <h1 className="sh-hero-title">Ready to Learn? 🚀</h1>
          <p className="sh-hero-sub">
            Pick a section below and start improving your English today.
          </p>
          <div className="sh-hero-pills">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                className="sh-hero-pill"
                onClick={() => { setOpenId(s.id); setTimeout(() => document.getElementById(`sh-${s.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); }}
              >
                {s.emoji} {s.items.length}
              </button>
            ))}
          </div>
        </div>

        {/* Accordion sections */}
        <div className="sh-sections">
          {SECTIONS.map((s) => (
            <div key={s.id} id={`sh-${s.id}`}>
              <Section
                section={s}
                open={openId === s.id}
                onToggle={() => toggle(s.id)}
                navigate={navigate}
              />
            </div>
          ))}
        </div>

      </div>
      <Footer />
    </>
  );
};

export default StudyHub;
